import React, { FC, useState, useEffect } from 'react';
import { Slider } from 'antd';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './FitnessLevelStep.scss';
import { motion } from 'motion/react';
import { AnimatedStep } from 'src/components/Animation/AnimatedStep';

const fitnessLevelDescriptions: Record<string, string[]> = {
  beginner: [
    'Little to no exercise experience',
    'Poor stamina, strength, or mobility',
    'May struggle with basic movements (e.g., squats, push-ups)',
    'Needs focus on form, consistency, and confidence',
  ],
  'early-intermediate': [
    'Some consistency (a few weeks/months of training)',
    'Basic technique understood',
    'Can handle light–moderate intensity',
    'Still improving coordination and endurance',
  ],
  intermediate: [
    'Regular training (6 to 24 months)',
    'Good technique in most exercises',
    'Can follow structured programs',
    'Starting to hit plateaus → needs progression strategies',
  ],
  advanced: [
    'Years of consistent training',
    'Strong, efficient movement patterns',
    'High work capacity',
    'Needs specific programming (periodization, performance goals)',
  ],
  athlete: [
    'Sport-specific training',
    'Very high performance level',
    'Focus on optimization, recovery, and marginal gains',
  ],
};

interface FitnessLevelStepProps {
  clientName?: string;
  value?: string;
  onChange?: (val: string) => void;
}

// What's {nameDisplay} fitness level?
export const FitnessLevelStep: FC<FitnessLevelStepProps> = ({ clientName, value, onChange }) => {
  const [localFitnessLevel, setLocalFitnessLevel] = useState<string>('beginner');
  const fitnessLevel = value !== undefined ? value : localFitnessLevel;

  useEffect(() => {
    if (value === undefined && onChange) {
      onChange('beginner');
    }
  }, [value, onChange]);

  const nameDisplay = clientName ? `${clientName}'s` : 'their';

  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const fitnessLevelData = programMasterData?.fitnessLevel;

  const defaultTitle = `What's ${nameDisplay} fitness level?`;
  const displayTitle = fitnessLevelData?.title
    ? fitnessLevelData.title.replace('{{clientName}}', nameDisplay)
    : defaultTitle;
  const displaySubtitle = fitnessLevelData?.subtitle;

  // Use the API options and ensure they are sorted by order
  const options = [...(fitnessLevelData?.options || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  if (isLoading) {
    return <div className="fitness-level-step">{'Loading options...'}</div>;
  }

  if (isError || !fitnessLevelData) {
    return <div className="fitness-level-step">{'Error loading options.'}</div>;
  }

  const sliderValue = Math.max(1, options.findIndex((opt) => opt.slug === fitnessLevel) + 1);

  return (
    <div className="fitness-level-step">
      <h2 className="step-title">{displayTitle}</h2>
      <p className="step-subtitle">{displaySubtitle}</p>

      <div className="fitness-card">
        <div className="slider-container">
          <Slider
            min={1}
            max={options.length || 5}
            step={1}
            dots={true}
            value={sliderValue}
            onChange={(val) => {
              const selectedOption = options[val - 1];
              if (selectedOption) {
                if (onChange) onChange(selectedOption.slug);
                setLocalFitnessLevel(selectedOption.slug);
              }
            }}
            tooltip={{ open: false }}
            className="fitness-slider"
          />
        </div>

        <div className="fitness-labels-desktop">
          {options.map((level) => {
            return (
              <div
                key={level.id}
                className={`fitness-label-item ${fitnessLevel === level.slug ? 'active' : ''}`}
                onClick={() => {
                  if (onChange) onChange(level.slug);
                  setLocalFitnessLevel(level.slug);
                }}
              >
                <span className="label-text">{level.title}</span>
              </div>
            );
          })}
        </div>

        <div className="fitness-labels-mobile">
          <div className="fitness-label-item active">
            <span className="label-text">{options[sliderValue - 1]?.title}</span>
          </div>
        </div>

        <AnimatedStep isVisible={true}>
          <div className="gradient-description-container" style={{ marginTop: -10 }}>
            <ul className="gradient-description-list" key={fitnessLevel}>
              {fitnessLevelDescriptions[fitnessLevel]?.map((description, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)' }}
                  animate={{ opacity: 0.7, clipPath: 'inset(0 0 0 0)' }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.8,
                    ease: 'easeInOut',
                  }}
                  className="gradient-description-item text-light slide-up-item"
                >
                  {description}
                </motion.li>
              ))}
            </ul>
          </div>
        </AnimatedStep>
      </div>
    </div>
  );
};
